import styled from 'styled-components';
import { COLORS, pickThemeFontStyles } from '@/theme';

export const PreviewShell = styled.div<{ $bannerBleed?: boolean }>`
  margin-top: 8px;
  border-radius: 8px;
  border: 1px solid ${COLORS.GRAY_94};
  background: ${COLORS.WHITE};
  overflow: hidden;
  min-height: 48px;
  ${({ $bannerBleed }) =>
    $bannerBleed
      ? `
    margin-left: -8px;
    margin-right: -8px;
    width: calc(100% + 16px);
  `
      : ''}
`;

export const PreviewBanner = styled.div<{ $src?: string }>`
  height: 176px;
  background: ${({ $src }) => ($src ? `center / cover no-repeat url(${$src})` : `${COLORS.GRAY_LIGHT}`)};
  position: relative;
`;

export const PreviewBannerBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 6px 10px;
  ${pickThemeFontStyles('12', '14', 'SEMI_NORMAL')};
  color: ${COLORS.WHITE};
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
  z-index: 1;
`;

export const PreviewBannerCta = styled.div<{ $aboveAltBar?: boolean }>`
  position: absolute;
  bottom: ${({ $aboveAltBar }) => ($aboveAltBar ? '34px' : '10px')};
  right: 10px;
  z-index: 2;
  max-width: calc(100% - 20px);
  padding: 5px 12px;
  border-radius: 4px;
  background: ${COLORS.YELLOW_PRIMARY};
  color: ${COLORS.BLACK_PRIMARY};
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  ${pickThemeFontStyles('12', '14', 'SEMI_BOLD')};
  word-break: break-word;
`;

export const PreviewGalleryRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  flex-wrap: wrap;
`;

/** Larger gallery tiles with click-to-preview (antd Image), similar to product photo grid. */
export const PreviewGalleryThumb = styled.div`
  width: 7.5rem;
  height: 7.5rem;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${COLORS.DIVIDER};
  background: ${COLORS.BRIGHT_GRAY};

  .ant-image {
    width: 100%;
    height: 100%;
  }

  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PreviewHeading = styled.div`
  ${pickThemeFontStyles('14', '18', 'SEMI_BOLD')};
  color: ${COLORS.BLACK_PRIMARY};
  padding: 8px 10px 4px;
`;

export const PreviewBody = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  padding: 0 10px 8px;
  max-height: 120px;
  overflow: auto;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 6px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 6px;
    padding-left: 1.1rem;
  }

  blockquote {
    margin: 0 0 6px;
    padding-left: 8px;
    border-left: 2px solid ${COLORS.SOFT_GRAY};
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

/** Plain multiline text: same typography as PreviewBody, preserves newlines like inspector TextArea. */
export const PreviewBodyPlain = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  padding: 0 10px 8px;
  max-height: 120px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

export const PreviewList = styled.ul`
  margin: 0 10px 8px 10px;
  padding-left: 1.1rem;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
`;

export const PreviewListBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 4px;
`;

export const PreviewListIntro = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  margin: 0 10px;
  max-height: 120px;
  overflow: auto;
  word-break: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 6px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 6px;
    padding-left: 1.1rem;
  }

  blockquote {
    margin: 0 0 6px;
    padding-left: 8px;
    border-left: 2px solid ${COLORS.SOFT_GRAY};
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

/** Plain intro under list heading — mirrors PreviewListIntro spacing with pre-wrap. */
export const PreviewListIntroPlain = styled.div`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  margin: 0 10px;
  max-height: 120px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

export const PreviewListItems = styled.ul`
  margin: 0 10px 8px 10px;
  padding-left: 1.1rem;
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  list-style-position: outside;

  & > li {
    margin-bottom: 6px;
    padding-left: 2px;
  }
`;

export const PreviewButtonPill = styled.span`
  display: block;
  box-sizing: border-box;
  max-width: 100%;
  margin: 8px 10px;
  padding: 6px 14px;
  border-radius: 4px;
  background: ${COLORS.YELLOW_PRIMARY};
  color: ${COLORS.BLACK_PRIMARY};
  text-align: left;
  word-break: break-word;
  overflow-wrap: anywhere;
  ${pickThemeFontStyles('12', '16', 'SEMI_BOLD')};
`;

export const PreviewTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px;
`;

export const PreviewTag = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  background: ${COLORS.GRAY_94};
  color: ${COLORS.CHARCOAL};
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
`;

/** Horizontal strip — similar to storefront product sliders / cards row */
export const PreviewProductSliderRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  overflow-x: auto;
  flex-wrap: nowrap;
`;

/** Card shell — mirrors storefront ItemCard (gray panel, border, soft shadow, hover lift) */
export const PreviewProductCard = styled.div`
  flex: 0 0 auto;
  width: 184px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${COLORS.GRAY};
  border: 1px solid ${COLORS.SOFT_GRAY};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 2px 2px 10px 0 rgba(233, 233, 233, 1), -2px -2px 10px 0 rgba(233, 233, 233, 1);
  transition: box-shadow 0.2s ease-out;

  &:hover {
    box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px -1px rgba(50, 50, 71, 0.05);
  }
`;

export const PreviewProductImageWrap = styled.div`
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  box-sizing: border-box;
  background: ${COLORS.GRAY};
`;

export const PreviewProductImg = styled.div<{ $src?: string }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 6px;
  background: ${({ $src }) => ($src ? `center / contain no-repeat url(${$src})` : `${COLORS.BRIGHT_GRAY}`)};
`;

/** Warm strip like delivery bar on storefront ItemCard */
export const PreviewProductAccentStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 6px 8px;
  background: ${COLORS.PALE_YELLOW};
  color: ${COLORS.CHARCOAL};
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  text-align: center;
  word-break: break-word;
`;

export const PreviewProductCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 10px 12px;
  flex: 1;
  min-height: 0;
  background: ${COLORS.GRAY};
`;

export const PreviewProductTitle = styled.div`
  ${pickThemeFontStyles('12', '16', 'SEMI_NORMAL')};
  color: ${COLORS.BLACK_PRIMARY};
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

export const PreviewProductPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  flex-wrap: wrap;
`;

export const PreviewProductPrice = styled.span`
  ${pickThemeFontStyles('14', '18', 'SEMI_BOLD')};
  color: ${COLORS.BLACK_PRIMARY};
`;

export const PreviewProductPriceUnit = styled.span`
  ${pickThemeFontStyles('10', '14', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
  margin-left: 2px;
`;

export const PreviewProductOldPrice = styled.span`
  ${pickThemeFontStyles('12', '14', 'NORMAL')};
  color: ${COLORS.GRAY_SECONDARY};
  text-decoration: line-through;
`;

export const PreviewPlaceholder = styled.div`
  padding: 12px 10px;
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
  color: ${COLORS.GREY};
  text-align: center;
`;
